import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button } from '@components/atoms/button';
import { ROUTES } from '@constants';
import { useToast } from '@hooks/use-toast';
import { useLazyGetVerificationByShortIdQuery } from '@modules/dashboard/api/verifications-api';
import type { RootState } from '@store/root-reducer';

export const PreviewButton: React.FC = () => {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const navigate = useNavigate();
    const organizationId = useSelector(
        (state: RootState) => state.dashboard.session.organizationId,
    );
    const id = useSelector((state: RootState) => state.docsVerification.sdkOptions?.id);
    const [getVerificationByShortId, { isLoading }] = useLazyGetVerificationByShortIdQuery();
    const errorMessage = useMemo(() => t('common.pleaseTryAgainLater'), [t]);
    const onPreviewVerificationClick = useCallback(async () => {
        try {
            if (!id) {
                return;
            }
            const response = await getVerificationByShortId({
                organizationId,
                id,
            }).unwrap();

            navigate(`${ROUTES.DOCVER.RESULT}${response.id}`);
        } catch (e) {
            showToast({
                variant: 'error',
                message: errorMessage,
            });
        }
    }, [navigate, organizationId, id, showToast, errorMessage, getVerificationByShortId]);

    return (
        <Button
            variant='tertiary'
            onClick={onPreviewVerificationClick}
            className='button-redirect'
            size='xl'
            loading={isLoading}
        >
            {t('docVerificationWizard.finish.checkVerificationDetails')}
        </Button>
    );
};
